import * as React from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
    Button,
    TextField,
    CircularProgress,
    Grid,
    Typography,
    Checkbox,
    FormControlLabel,
    FormControl,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Paper,
    PaperProps
} from "@mui/material";
import { useState, useEffect } from "react";
import { useDataProvider, useRefresh } from "react-admin";
import Transformer from "src/common/Transformer";
import { OrderItemProps, OrderProps } from "src/resources/sales/interface";
import { useForm } from "react-hook-form";
import ApiResources from "src/providers/ApiResources";
import {
    AddressFormAutocomplete
} from "src/components/address";
import { SelectField } from "src/components/input";
import Draggable from 'react-draggable';

interface AddItemModalProps {
    open: boolean;
    setOpen: any;
    record: OrderProps;
}

const PaperComponent = (props: PaperProps) => {
    return (
        <Draggable
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} />
        </Draggable>
    );
}

export const AddItemModal = (props: AddItemModalProps) => {
    const { open, setOpen, record } = props;

    const [addRingset, setAddRingset] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [productLoading, setProductLoading] = useState(false);
    const { register, unregister, reset, control, handleSubmit, getValues, setValue, formState: { errors }} = useForm<OrderItemProps>();
    const dataProvider = useDataProvider();
    const refresh = useRefresh();
    const [options, setOptions] = useState([]);

    useEffect(() => {
        getProducts();
    }, []);

    const getGroupProducts = (productId: number) => {
        dataProvider
        .getList(ApiResources.getProducts(false), {
            filter: { parent: productId },
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'id', order: 'DESC' },
        })
        .then((res) => {
            if(res.data !== undefined){  
                setOptions(res.data);
            }
        });
    }

    const getProducts = (query?: string) => {
        setProductLoading(true);
        dataProvider
        .getList(ApiResources.getProducts(false), {
            filter: { search: query },
            pagination: { page: 1, perPage: 10 },
            sort: { field: 'parent.id', order: 'ASC' },
        })
        .then((res) => {
            setProductLoading(false);
            if(res.data !== undefined){  
                setOptions(res.data);
            }
        });
    }

    const transform = (data) => {
        Transformer(data, {
            remove: [
                "createdAt", 
                "id", 
                "updatedAt", 
                "originalPrice", 
                "price", 
                "orderIncrementId", 
                "subTotal", 
                "total",
                "taxPercent", 
                "taxAmount",
            ],
            update: {
                product: data.product.id
            },
        });
        return data;
    };

    
    const add = async (data: OrderItemProps) => {
        try {
            let formData = {...data};

            if (!addRingset) {
                delete formData['group_items'];
            }

            setLoading(true);
            setError(null);
            dataProvider
            .create(ApiResources.postOrderItem(record.id, false), {
                data: transform({...formData}),
            })
            .then((res) => {
                setLoading(false);
                reset();
                setAddRingset(false);
                refresh();
                setOpen(false);
            })
            .catch((error) => {
                setError(error.message);
                setLoading(false);
            });
        } catch (e) {
            console.log(e);
        }
    }

    const handleAddRingset = (value: boolean) => {
        setAddRingset(value);

        if (false === value) {
            unregister("group_items");
        }
    }

    return (
        <Dialog
            onClose={() => setOpen(false)}
            open={open}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            fullWidth={true}
            maxWidth="sm"
        >
            <form onSubmit={handleSubmit(add)}>
            <DialogTitle sx={{padding: "18px"}}>
                <Typography variant="body1" sx={{fontSize: "20px", fontWeight: "500"}}>Add New Item</Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => setOpen(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                    >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{padding: "18px !important"}}>
                <Grid container item sm={12} rowSpacing={1} columnSpacing={2}>
                    <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                        <AddressFormAutocomplete
                            label={"Product"}
                            size="small"
                            required
                            error={!!errors.product}
                            helperText={errors.product?.message}
                            loading={productLoading}
                            filterOptions={(x) => x}
                            getOptionLabel={(option) => `${option?.name} ${option?.sku}`}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            options={options}
                            {...register("product", {
                                required: "Field is required",
                            })}
                            onInputChange={(e, value) => {
                                getProducts(value);
                            }}
                            onChange={(e, value) => {
                                setValue('product', value, { shouldValidate: true });
                                getGroupProducts(value.id);
                                setValue('group_items', []);
                            }}
                        />
                    </Grid> 

                    <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="Quantity"
                            variant="outlined"
                            size="small"
                            required
                            error={!!errors.quantity}
                            helperText={errors.quantity?.message}
                            {...register("quantity", {
                                required: "Field is required",
                                pattern: {
                                    value: /^\d+(.\d){0,1}$/,
                                    message: "A quantity can contain only one decimal number."
                                }
                            })}
                        />
                    </Grid> 

                    <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="Note"
                            variant="outlined"
                            size="small"
                            error={!!errors.note}
                            helperText={errors.note?.message}
                            {...register("note")}
                        />
                    </Grid>

                    <Grid item sm={12} md={12}>
                        <FormControl>
                            <FormControlLabel control={
                                <Checkbox
                                    value={addRingset}
                                    onChange={(e) => handleAddRingset(!addRingset)}  
                                />
                            } label="Add Ringset?" />
                        </FormControl>
                    </Grid>

                    {(getValues("product") && addRingset) &&
                    <Grid item sm={12} md={12} sx={{marginBottom: "18px"}}>
                        <SelectField
                            label="Ringset Items"
                            size="small"
                            value={getValues("group_items") || []}
                            multiple
                            error={!!errors.group_items}
                            helperText={(errors.group_items as any)?.message}
                            required
                            choices={options.map((product) => {return {id: product.id, name: product.sku}})}
                            {...register("group_items", {
                                required: "Field is required"
                            })}
                            onChange={(e) => {
                               setValue('group_items', e.target.value, { shouldValidate: true });
                            }}
                        />
                    </Grid> 
                    }

                    { error &&
                    <Grid item sm={12} md={12} sx={{display: "flex", marginTop: "18px"}}>
                        <Typography variant="subtitle2" color="error">{error}</Typography>
                    </Grid>                     
                    } 
                </Grid>
            </DialogContent>
            <DialogActions sx={{padding: "18px"}}>
                <Button
                    variant="outlined"
                    onClick={() => {
                    setOpen(false);
                    }}
                >
                    Cancel
                </Button>
                <Button variant="contained" type="submit" disabled={loading}>
                    {loading && <CircularProgress size={18} thickness={2} sx={{ fontSize: "18px", color: "#ffffff", marginRight: "8px"}}/>}
                    Add
                </Button>
            </DialogActions>
            </form>
        </Dialog>
    );
};
